import { useMemo } from 'react';
import classNames from 'classnames';

import { Button } from '../../button/button.component';
import { ImageEmbedded } from '../../image/image-embedded';
import { Text } from '../../text/text.component';
import { WistiaVideoProps } from '../../wistia-video/wistia-video.config';
import { replaceWhiteSpacesWithUnderscore } from '../../../utils/strings';
import { withAsyncImport } from '../../../utils/with-async-import';

import { VerticalTabsContentBlockProps } from './vertical-tabs-content-block.config';

const WistiaVideo = withAsyncImport<WistiaVideoProps>(
  () => import(/* webpackChunkName: "components" */ '../../wistia-video/wistia-video.component'),
  false,
  'vertical-tabs-content-block-wistia-video'
);

const VerticalTabsContentBlock = ({
  'cq:panelTitle': panelTitle,
  cta,
  hasGradientBackground,
  hasMidBlueBackground,
  image,
  isInEditor,
  text,
  wistia_video: wistiaVideo,
}: VerticalTabsContentBlockProps) => {
  const darkBg = hasGradientBackground || hasMidBlueBackground;

  const styles = useMemo(
    () => ({
      cta: {
        'snowflake-button-primary': true,
        'snowflake-button-compact': true,
        'snowflake-button-blue': !darkBg,
        'snowflake-button-white': darkBg,
      },
      text: {
        'snowflake-vertical-tabs-content-block-text': true,
        'text-size-regular': true,
        'text-color-text-04': hasMidBlueBackground,
        'text-color-text-04-ui-bg-03': hasGradientBackground,
      },
    }),
    [darkBg, hasMidBlueBackground, hasGradientBackground]
  );

  return (
    <div
      data-testing={replaceWhiteSpacesWithUnderscore(panelTitle)?.toLowerCase()}
      className="snowflake-vertical-tabs-content-block"
      data-testid="snowflake-vertical-tabs-content-block"
    >
      {image && (
        <div
          className="snowflake-vertical-tabs-content-block-image"
          data-testid="snowflake-vertical-tabs-content-block-image"
        >
          <ImageEmbedded {...image} baseCssClass="cmp-image" />
        </div>
      )}
      {wistiaVideo && (
        <div
          className="snowflake-vertical-tabs-content-block-video"
          data-testid="snowflake-vertical-tabs-content-block-video"
        >
          <WistiaVideo {...wistiaVideo} isInEditor={isInEditor} />
        </div>
      )}
      {text && (
        <div className={classNames(styles.text)} data-testid="snowflake-vertical-tabs-content-block-text">
          <Text {...text} />
        </div>
      )}
      {cta && (
        <div className={classNames(styles.cta)} data-testid="snowflake-vertical-tabs-content-block-cta">
          <Button {...cta} />
        </div>
      )}
    </div>
  );
};

export default VerticalTabsContentBlock;
